import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShoppingCart, Play, ChevronLeft, Lock } from "lucide-react";
import { WorkoutPlayer } from "@/components/program/WorkoutPlayer";
import { CheckIn } from "@/components/program/CheckIn";

export const Plan = ({ accessId }) => {
  const [road, setRoad] = useState(null);
  const [wk, setWk] = useState(null);
  const [week, setWeek] = useState(null);
  const [grocery, setGrocery] = useState(null);
  const [player, setPlayer] = useState(null);
  const [checkin, setCheckin] = useState(false);

  const loadRoad = () => api.get(`/programs/${accessId}/roadmap`).then(({ data }) => setRoad(data)).catch(() => {});
  useEffect(() => { loadRoad(); /* eslint-disable-next-line */ }, [accessId]);

  const openWeek = async (n) => { const { data } = await api.get(`/programs/${accessId}/week/${n}`); setWeek(data); setWk(n); window.scrollTo(0, 0); };
  const openGrocery = async (n) => { const { data } = await api.get(`/programs/${accessId}/grocery/${n}`); setGrocery(data); };

  if (!road) return <div className="pt-10 font-mono text-xs text-cyan-300 animate-pulse">Loading…</div>;

  if (week) {
    return (
      <div className="pt-6" data-testid="program-week-detail">
        <button onClick={() => { setWeek(null); setWk(null); }} className="flex items-center gap-1 text-sm text-slate-400 mb-3" data-testid="week-back"><ChevronLeft size={16} /> Roadmap</button>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div><div className="mono-label">{week.phase_name}</div><h1 className="font-serif text-3xl text-white">Week {week.week_number}</h1><p className="text-sm text-slate-400 mt-1">{week.goal}</p></div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => openGrocery(week.week_number)} data-testid="grocery-btn"><ShoppingCart size={14} className="mr-2" /> Grocery list</Button>
            <Button onClick={() => setCheckin(true)} className="btn-primary rounded-lg" data-testid="open-checkin">Weekly check-in</Button>
          </div>
        </div>
        <div className="mt-5 space-y-3">
          {week.days.map((d) => (
            <div key={d.day_index} className="glass rounded-2xl p-4" data-testid={`plan-day-${d.day_index}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-white font-medium">{d.day_name}</div>
                <div className="text-[11px] font-mono text-slate-500">{d.progress?.done}/{d.progress?.total}</div>
              </div>
              <div className="space-y-1.5">
                {d.tasks.map((t) => (
                  <div key={t.id} className="flex items-center justify-between gap-2 text-[13px]">
                    <span className={`${t.done ? "text-slate-500 line-through" : "text-slate-300"}`}>{t.title}</span>
                    {t.type === "training" && t.detail?.exercises && (
                      <Button size="sm" variant="outline" onClick={() => setPlayer({ ...t, week_number: week.week_number, day_index: d.day_index })} data-testid={`start-workout-${d.day_index}`}>
                        <Play size={12} className="mr-1" /> Start
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        {player && <WorkoutPlayer accessId={accessId} task={player} onClose={() => { setPlayer(null); openWeek(week.week_number); }} />}
        {checkin && <CheckIn accessId={accessId} weekNumber={week.week_number} onClose={() => setCheckin(false)} onDone={() => { setCheckin(false); loadRoad(); }} />}
        <Grocery grocery={grocery} onClose={() => setGrocery(null)} />
      </div>
    );
  }

  return (
    <div className="pt-6" data-testid="program-plan">
      <div className="mono-label">Your journey</div>
      <h1 className="font-serif text-4xl text-white mb-4">Program Roadmap</h1>
      <div className="space-y-6">
        {road.phases.map((p) => (
          <div key={p.n}>
            <div className="text-sm text-cyan-300 font-mono uppercase tracking-widest mb-2">Phase {p.n} · {p.name}</div>
            <p className="text-[12px] text-slate-500 mb-3">{p.focus}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {road.weeks.filter((w) => w.phase === p.n).map((w) => (
                <button key={w.week_number} onClick={() => openWeek(w.week_number)} data-testid={`roadmap-week-${w.week_number}`}
                  className={`glass rounded-xl p-3 text-left border ${w.status === "current" ? "border-cyan-400/70 bg-cyan-400/10" : "border-transparent"}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs text-white">Week {w.week_number}</span>
                    {w.locked && <Lock size={12} className="text-slate-500" />}
                  </div>
                  <div className={`text-[9px] font-mono uppercase tracking-widest mt-1 ${w.status === "completed" ? "text-emerald-400" : w.status === "current" ? "text-cyan-300" : "text-slate-600"}`}>{w.status}</div>
                  <div className="h-1 rounded-full bg-white/10 mt-2 overflow-hidden"><div className="h-full bg-cyan-400" style={{ width: `${w.pct}%` }} /></div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Grocery = ({ grocery, onClose }) => (
  <Dialog open={!!grocery} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="glass-strong border-white/10 max-w-lg" data-testid="grocery-modal">
      <DialogHeader><DialogTitle className="font-serif text-2xl text-white">Grocery List · Week {grocery?.week_number}</DialogTitle></DialogHeader>
      <div className="max-h-[60vh] overflow-y-auto space-y-4">
        {grocery && Object.entries(grocery.categories).map(([cat, items]) => (
          <div key={cat}>
            <div className="mono-label mb-1.5">{cat}</div>
            <div className="space-y-1">
              {items.map((it, i) => (
                <label key={i} className="flex items-center gap-2 text-[13px] text-slate-300"><input type="checkbox" className="accent-cyan-400" /> {it.item} {it.qty > 1 && <span className="text-slate-500 font-mono">×{it.qty}</span>}</label>
              ))}
            </div>
          </div>
        ))}
      </div>
    </DialogContent>
  </Dialog>
);
